"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { Activity, Loader2, LogOut, Settings } from "lucide-react";
import Image from "next/image";
import Link from "next/link";

import { useAuthContext } from "@/shared/auth";
import { cn } from "@/shared/lib/utils/cn";
import { useSidebarChats } from "@/features/chat/multi/sidebar/useSidebarChats";
import { SidebarChatList } from "@/features/chat/multi/sidebar/SidebarChatList";
import { InsightsSidebarList } from "@/features/insights";
import {
  chatActivityBannerLabel,
  getChatActivity,
  resolveChatActivityStatus,
  subscribeChatSignals,
  type ChatActivity,
} from "@/features/chat/multi/signals/chat-signals";
import { ChatRowStatusDot } from "@/features/chat/multi/sidebar/ChatRowStatusDot";
import { isSessionTracesEnabled } from "@/features/dev-trace/session-traces-enabled";
import { useDevTraceStore } from "@/features/dev-trace/dev-trace-store";
import { AunixLogo } from "@/shared/ui/AunixLogo";
import { Avatar, AvatarFallback } from "@/shared/ui/shadcn-components/avatar";
import { Button } from "@/shared/ui/shadcn-components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/ui/shadcn-components/dropdown-menu";
import { SimpleTooltip } from "@/shared/ui/simple-tooltip";

interface AppSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

function useChatActivity(): ChatActivity {
  const [activity, setActivity] = useState(getChatActivity);
  useEffect(() => subscribeChatSignals(() => setActivity(getChatActivity())), []);
  return activity;
}

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] ?? ""}${parts[1][0] ?? ""}`.toUpperCase();
}

export function AppSidebar({ collapsed, onToggle }: AppSidebarProps) {
  const router = useRouter();
  const { authUser, logout } = useAuthContext();
  const sidebarUser = {
    name: authUser?.name || "Signed in",
    role: authUser?.tenantName || authUser?.role || authUser?.email || "",
    initials: initialsFromName(authUser?.name || "Aunix"),
  };
  const {
    pathname,
    query,
    setQuery,
    chatsReady,
    filtered,
    visibleChats,
    hasMoreChats,
    loadingMore,
    activeSessionId,
    loadMoreRef,
    chatScrollAreaRef,
    activeChatRef,
    startNewDraft,
    openChat,
    openWorkflowCanvas,
    pinChat,
    renameChat,
    shareChat,
    deleteChat,
  } = useSidebarChats(collapsed);
  const activity = useChatActivity();
  const busyLabel = chatActivityBannerLabel(activity);
  const activityStatus = resolveChatActivityStatus(activity);
  const tracesEnabled = isSessionTracesEnabled();
  const brokenTrace = useDevTraceStore((state) => state.broken);

  const isAgents = pathname.startsWith("/agents");
  const isConnections = pathname.startsWith("/connections");
  const isInsights = pathname.startsWith("/insights");
  const isNewDraft = (pathname === "/home" || pathname === "/") && !activeSessionId;

  return (
    <aside
      className={cn(
        "flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-foreground/10",
        "bg-background shadow-[0_8px_30px_rgba(17,17,19,0.06)] transition-all duration-200",
        collapsed ? "w-14" : "w-[264px]",
      )}
    >
      <div
        className={cn(
          "flex shrink-0 items-center border-b border-sidebar-border px-3 py-3",
          collapsed ? "justify-center" : "justify-between",
        )}
      >
        {!collapsed ? (
          <SimpleTooltip content="Go to home">
            <Link href="/home" onClick={startNewDraft} className="ml-1 shrink-0">
              <AunixLogo className="dark:brightness-0 dark:invert" priority />
            </Link>
          </SimpleTooltip>
        ) : null}
        <SimpleTooltip content={collapsed ? "Expand sidebar" : "Collapse sidebar"}>
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            className="rounded-lg"
            onClick={onToggle}
          >
            <Image
              src="/figma-assets/sidebar-toggle.svg"
              alt=""
              width={16}
              height={16}
              className="size-4 dark:brightness-0 dark:invert"
            />
          </Button>
        </SimpleTooltip>
      </div>

      <div className="shrink-0 px-2 pb-1 pt-2">
        <Link
          href="/agents"
          className={cn(
            "mb-0.5 flex w-full items-center rounded-lg px-3 py-2 transition-all",
            collapsed ? "justify-center gap-0" : "gap-2.5",
            isAgents
              ? "bg-accent text-primary"
              : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
          )}
        >
          <Image
            src="/figma-assets/agents.svg"
            alt=""
            width={16}
            height={16}
            className="size-4 shrink-0 dark:brightness-0 dark:invert"
          />
          {!collapsed ? <span className="text-[13px] font-medium">Agents</span> : null}
        </Link>
        <Link
          href="/connections"
          className={cn(
            "mb-0.5 flex w-full items-center rounded-lg px-3 py-2 transition-all",
            collapsed ? "justify-center gap-0" : "gap-2.5",
            isConnections
              ? "bg-accent text-primary"
              : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
          )}
        >
          <Image
            src="/figma-assets/connections.svg"
            alt=""
            width={16}
            height={16}
            className="size-4 shrink-0 dark:brightness-0 dark:invert"
          />
          {!collapsed ? <span className="text-[13px] font-medium">Connections</span> : null}
        </Link>
        <Link
          href="/insights"
          className={cn(
            "mb-0.5 flex w-full items-center rounded-lg px-3 py-2 transition-all",
            collapsed ? "justify-center gap-0" : "gap-2.5",
            isInsights
              ? "bg-accent text-primary"
              : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
          )}
        >
          <Activity className="size-4 shrink-0" />
          {!collapsed ? <span className="text-[13px] font-medium">Insights</span> : null}
        </Link>
        <Link
          href="/home"
          onClick={startNewDraft}
          className={cn(
            "mb-0.5 flex w-full items-center rounded-lg px-3 py-2 transition-all",
            collapsed ? "justify-center gap-0" : "gap-2.5",
            isNewDraft
              ? "bg-muted/80 text-foreground"
              : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
          )}
        >
          <Image
            src="/figma-assets/new-draft.svg"
            alt=""
            width={16}
            height={16}
            className="size-4 shrink-0 dark:brightness-0 dark:invert"
          />
          {!collapsed ? <span className="text-[13px] font-medium">New Draft</span> : null}
        </Link>

        {busyLabel && activityStatus ? (
          collapsed ? (
            <SimpleTooltip content={busyLabel}>
              <div className="mx-auto mt-1 flex size-8 items-center justify-center rounded-lg border border-border/70 bg-background text-primary">
                {activityStatus === "needs_input" ? (
                  <ChatRowStatusDot status="needs_input" />
                ) : (
                  <Loader2 className="size-3.5 animate-spin" />
                )}
              </div>
            </SimpleTooltip>
          ) : (
            <div className="mt-1 flex items-center gap-2 rounded-lg border border-border/70 bg-background px-3 py-1.5 text-[11px] font-medium text-foreground">
              {activityStatus === "needs_input" ? (
                <ChatRowStatusDot status="needs_input" />
              ) : (
                <Loader2 className="size-3 shrink-0 animate-spin text-primary" />
              )}
              <span className="truncate">{busyLabel}</span>
            </div>
          )
        ) : null}
      </div>

      {isInsights ? (
        <InsightsSidebarList collapsed={collapsed} />
      ) : (
        <SidebarChatList
          collapsed={collapsed}
          pathname={pathname}
          chatsReady={chatsReady}
          query={query}
          onQueryChange={setQuery}
          filteredCount={filtered.length}
          visibleChats={visibleChats}
          hasMoreChats={hasMoreChats}
          loadingMore={loadingMore}
          activeSessionId={activeSessionId}
          loadMoreRef={loadMoreRef}
          chatScrollAreaRef={chatScrollAreaRef}
          activeChatRef={activeChatRef}
          onOpenChat={openChat}
          onOpenWorkflowCanvas={openWorkflowCanvas}
          onPinChat={pinChat}
          onRenameChat={renameChat}
          onShareChat={shareChat}
          onDeleteChat={deleteChat}
        />
      )}

      <div className="shrink-0 border-t border-sidebar-border px-3 py-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={cn(
                "flex w-full items-center rounded-xl border border-transparent transition-colors hover:border-border/50 hover:bg-muted/60 hover:shadow-sm",
                collapsed ? "justify-center px-0 py-2" : "gap-2.5 px-2.5 py-2",
              )}
            >
              <Avatar
                className="size-8 after:border-transparent"
                style={{
                  background:
                    "linear-gradient(135deg, var(--primary) 0%, color-mix(in oklch, var(--primary) 80%, white) 100%)",
                }}
              >
                <AvatarFallback className="bg-transparent text-[12px] font-semibold text-white">
                  {sidebarUser.initials}
                </AvatarFallback>
              </Avatar>
              {!collapsed ? (
                <>
                  <div className="min-w-0 flex-1 text-left">
                    <p className="truncate text-[13px] leading-tight font-medium text-foreground">
                      {sidebarUser.name}
                    </p>
                    <p className="truncate text-[11px] leading-tight text-muted-foreground/60">
                      {sidebarUser.role}
                    </p>
                  </div>
                  <span className="relative shrink-0">
                    <Settings className="size-3.5 text-muted-foreground/40" />
                    {tracesEnabled && brokenTrace ? (
                      <span className="absolute -right-0.5 -top-0.5 size-1.5 rounded-full bg-red-500" />
                    ) : null}
                  </span>
                </>
              ) : null}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align={collapsed ? "center" : "end"} side="top" className="w-48">
            <DropdownMenuLabel className="text-[11px] font-medium text-muted-foreground">
              Settings
            </DropdownMenuLabel>
            {tracesEnabled ? (
              <DropdownMenuItem
                onSelect={() => useDevTraceStore.getState().setOpen(true)}
                className="gap-2"
              >
                <Activity className="size-3.5" />
                Chat traces
                {brokenTrace ? <span className="ml-auto size-1.5 rounded-full bg-red-500" /> : null}
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              variant="destructive"
              className="gap-2"
              onSelect={() => {
                void logout().then(() => {
                  router.replace("/login");
                  router.refresh();
                });
              }}
            >
              <LogOut className="size-3.5" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
}
